import { GATEWAY_URL } from "../config";

// Gateway-backed repository. The tablet's learner and classroom are never sent:
// the gateway takes them from the tablet's own cookie and scopes every read
// and write to them (handoff §15). Nothing on this side is authorization.

export class GatewayError extends Error {
  constructor(status, reason) {
    super(reason || `gateway answered ${status}`);
    this.response = { status };
    this.reason = reason || null;
  }
}

const call = async (path, { method = "GET", json, body, type } = {}) => {
  // A build without the gateway's address cannot work; say so as a setup
  // fault rather than asking this app's own server.
  if (!GATEWAY_URL) throw new GatewayError(503, "gateway-not-configured");
  const response = await fetch(`${GATEWAY_URL}${path}`, {
    method,
    credentials: "include",
    headers:
      json !== undefined
        ? { "Content-Type": "application/json" }
        : type
          ? { "Content-Type": type }
          : {},
    body: json !== undefined ? JSON.stringify(json) : body,
  });
  const data =
    response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) throw new GatewayError(response.status, data?.reason);
  return data;
};

const withAssetUrls = (activity) => activity && ({
  ...activity,
  resolved_assets: Object.fromEntries(
    Object.entries(activity.resolved_assets || {}).map(([key, asset]) => [
      key,
      {
        ...asset,
        src: asset.src?.startsWith("/") ? `${GATEWAY_URL}${asset.src}` : asset.src,
      },
    ])
  ),
});

const liveRepository = {
  // A tablet that was never set up, or was reset, has no cookie.
  isSignedIn: async () => (await call("/tablet/session")).provisioned === true,

  // Exchanges a setup code, once, for this tablet's learner cookie.
  provision: (code) =>
    call("/tablet/provision", { method: "POST", json: { code } }),

  async signOut() {
    try {
      await call("/tablet/reset", { method: "POST" });
    } catch {
      // Nothing more this side can do; the next check will say.
    }
  },

  isEnrolled: async () => (await call("/learner/enrolment")).enrolled,

  currentRelease: async () => (await call("/learner/release")).release,

  readActivity: async (activityId) =>
    withAssetUrls(
      (await call(`/learner/activities/${encodeURIComponent(activityId)}`))
        .activity
    ),

  // This tablet's attempt at the released activity, if it has begun one.
  findAttempt: async ({ deliveryId, activityId }) =>
    (
      await call(
        `/learner/attempt?delivery=${encodeURIComponent(deliveryId)}&activity=${encodeURIComponent(activityId)}`
      )
    ).attempt,

  // The gateway derives the same deterministic key and gets-or-creates.
  createAttempt: async ({ deliveryId, activityId }) =>
    (
      await call("/learner/attempts", {
        method: "POST",
        json: { deliveryId, activityId },
      })
    ).attempt,

  // Hands in one step's response. The gateway validates it, records it once by
  // `submissionId`, judges it and decides whether the learner moves on; this
  // side never claims correctness or completion (Handoff 007 §3).
  submitStep: async (attemptId, stepId, { submissionId, response }) =>
    call(
      `/learner/attempts/${encodeURIComponent(attemptId)}/steps/${encodeURIComponent(stepId)}/submissions`,
      { method: "POST", json: { submissionId, response } }
    ),

  // Homework, practice and assessment the teacher has set for this learner,
  // already limited by the gateway to this tablet's class, audience and dates.
  listAssignments: async () => (await call("/learner/assignments")).assignments || [],

  // The assignment with its approved activity. Expected answers are absent
  // whenever the assignment's feedback policy withholds them.
  readAssignment: async (assignmentId) => {
    const data = await call(`/learner/assignments/${encodeURIComponent(assignmentId)}`);
    return { assignment: data.assignment, activity: withAssetUrls(data.activity) };
  },

  // Get-or-create the one attempt for this learner and assignment.
  startAssignment: async (assignmentId) =>
    (
      await call(`/learner/assignments/${encodeURIComponent(assignmentId)}/attempt`, {
        method: "POST",
        json: {},
      })
    ).attempt,

  // Completed work this learner really did, newest first.
  myLearning: async () => (await call("/learner/my-learning")).activities || [],

  // A finished attempt as it was done, for read-only review and practice.
  readRevision: async (attemptId) => {
    const data = await call(`/learner/attempts/${encodeURIComponent(attemptId)}/revision`);
    return { attempt: data.attempt, activity: data.activity };
  },

  // Uploads the picture and links it as drawing evidence, once. Sending it
  // again after a lost response finds what is already there.
  saveDrawing: ({ attempt, stepId, blob }) =>
    call(
      `/learner/attempts/${encodeURIComponent(attempt.id)}/drawings/${encodeURIComponent(stepId)}`,
      { method: "PUT", type: "image/png", body: blob }
    ),
};

export default liveRepository;
